import { useEffect, useRef, useState } from 'react'

import { datadogRum } from '@datadog/browser-rum'
import {
  Collapse,
  Divider,
  Text,
  Loader,
  ActionIcon,
  Tooltip,
} from '@mantine/core'
import { useForm, type FormErrors } from '@mantine/form'
import {
  Check,
  ChevronDown,
  ChevronRight,
  X,
  Pencil,
  ExternalLink,
  MailX,
  Phone,
  PhoneOff,
  Send,
} from 'lucide-react'
import { zodResolver } from 'mantine-form-zod-resolver'
import {
  CarrierByDotNumber,
  CarrierByDotNumberVariables,
  VerificationOfEmployment,
} from 'types/graphql'
import { z } from 'zod'

import { useMutation, useQuery } from '@redwoodjs/web'

import StyledBadge from 'src/components/DataDisplay/StyledBadge/StyledBadge'
import { trackContactAdded } from 'src/components/Inputs/CarrierAutocomplete/contactTelemetry'
import { KnownContact } from 'src/components/Inputs/CarrierAutocomplete/knownContacts'
import { useKnownCarrierContacts } from 'src/components/Inputs/CarrierAutocomplete/useKnownCarrierContacts'
import {
  FormCompanyDotField,
  FormCompanyField,
  FormContactField,
  FormPhoneField,
  FormTextField,
} from 'src/components/Overlays/EmploymentDetailsDrawer/components/FormComponents'
import KnownContactsBlock from 'src/components/Overlays/EmploymentDetailsDrawer/components/KnownContactsBlock'
import { toast } from 'src/components/Overlays/Toast/Toast'
import CarrierDirectoryLink from 'src/components/VoE/CarrierDirectoryLink/CarrierDirectoryLink'
import OutreachPreviewLine from 'src/components/VoE/OutreachPreviewLine/OutreachPreviewLine'
import { GET_CARRIER_BY_DOT_NUMBER } from 'src/graphql/carriers.graphql'
import { VoEStatus } from 'src/graphql/types/verificationOfEmployments'
import { UPDATE_VOE, UPDATE_VOE_REFERRAL } from 'src/graphql/voe.graphql'
import { useCompanyOutreachConfig } from 'src/hooks/useCompanyOutreachConfig'
import { formatAddress } from 'src/lib/address.utils'
import { isDialableUsNumber } from 'src/lib/contact.utils'
import { isValidEmail } from 'src/lib/email.utils'
import { formatDateCustom } from 'src/lib/formatters'
import {
  formatInternationalPhoneNumber,
  removeCountryCode,
  unformatPhoneNumber,
  usDigits,
} from 'src/lib/phone.utils'
import { buildFmcsaCarrierUrl } from 'src/lib/url.utils'
import { outreachPreview } from 'src/lib/voe/outreachPreview'
import { orderPlatforms, voeProviderLabel } from 'src/lib/voeProvider.utils'

import VoEVoiceAgentButton from '../VoEVoiceAgentButton/VoEVoiceAgentButton'

/**
 * A phone number the voice agent was referred to on an earlier call and that
 * has not been applied to the VoE. Derived from the audit trail by the drawer.
 */
export type VoEReferral = {
  /** The audit-trail row the referral lives on; edits are written there. */
  auditTrailId?: string | null
  phoneNumber: string
  extension?: string | null
  referredFor?: string | null
  referredContactName?: string | null
  /** Who gave the number: the contact reached on that call. */
  givenBy?: string | null
  contactDate?: string | Date | null
  /**
   * True when the referral is the same number as the VoE's Phone (a recruiter
   * applied it, or edited it to match). The row is still shown so an edit
   * never looks like a lost save, but the call actions move to the Phone row.
   */
  matchesPhone?: boolean
}

export const describeReferral = (referral: VoEReferral): string => {
  const who = referral.givenBy?.trim()
  const when = referral.contactDate
    ? formatDateCustom(referral.contactDate, 'MM/DD')
    : null
  const purpose = [
    referral.referredFor?.trim(),
    referral.referredContactName?.trim()
      ? `ask for ${referral.referredContactName.trim()}`
      : null,
  ]
    .filter(Boolean)
    .join(' · ')
  return [
    'Given',
    who ? `by ${who}` : null,
    when ? `on ${when}` : null,
    purpose ? `for ${purpose}` : null,
  ]
    .filter(Boolean)
    .join(' ')
}

/** RUM `component` for the contact events this section emits. */
const CONTACT_COMPONENT = 'VerificationDetailsSection'

type VoeContactDraft = {
  dotNumber: string
  contactName: string
  email: string
  phoneNumber: string
  faxNumber: string
}

type VoeDetailsFormValues = VoeContactDraft & {
  referredPhone: string
  referredExtension: string
}

/** Trimmed text match. Phone fields use `samePhone` instead. */
const sameText = (next: string, current?: string | null) =>
  next.trim() === (current ?? '').trim()

/**
 * The phone input rewrites a stored number into its display form on mount.
 * Digits decide whether the recruiter actually changed it. A blank is a clear.
 */
const samePhone = (next: string, current?: string | null) =>
  usDigits(next) === usDigits(current)

/**
 * Fields the recruiter changed. The API leaves a missing key untouched and
 * treats '' as clear, so resending the drawer's blanks would wipe a contact
 * detail that arrived after the form was filled in.
 */
const changedVoeInput = (
  draft: VoeContactDraft,
  current: {
    dotNumber?: string | null
    contactName?: string | null
    email?: string | null
    phoneNumber?: string | null
    faxNumber?: string | null
  }
): Partial<VoeContactDraft> => {
  const input: Partial<VoeContactDraft> = {}
  if (!sameText(draft.dotNumber, current.dotNumber)) {
    input.dotNumber = draft.dotNumber
  }
  if (!sameText(draft.contactName, current.contactName)) {
    input.contactName = draft.contactName
  }
  if (!sameText(draft.email, current.email)) {
    input.email = draft.email
  }
  if (!samePhone(draft.phoneNumber, current.phoneNumber)) {
    input.phoneNumber = draft.phoneNumber
  }
  if (!samePhone(draft.faxNumber, current.faxNumber)) {
    input.faxNumber = draft.faxNumber
  }
  return input
}

/** Empty contact fields are absent, not a request to clear what is stored. */
const nonBlank = (value?: string | null): string | null => {
  const trimmed = value?.trim()
  return trimmed ? trimmed : null
}

/** Statuses the chain never emails or calls from. */
const OUTREACH_CLOSED_STATUSES: string[] = [
  VoEStatus.COMPLETED,
  VoEStatus.CANCELED,
  VoEStatus.DECLINED,
]

interface VerificationDetailsSectionProps {
  voe: VerificationOfEmployment
  referral?: VoEReferral | null
  onRender?: { query: any; variables?: any }[]
  onCall?: (phoneNumber?: string) => void
  onEmail?: () => void
  guardContact?: (action: () => void) => void
  companyAddress?: { street?: string; city?: string; state?: string } | null
}

const VerificationDetailsSection = ({
  voe,
  onRender,
  referral,
  onCall,
  onEmail,
  guardContact,
  companyAddress,
}: VerificationDetailsSectionProps) => {
  const [isEditing, setIsEditing] = useState(false)
  const [isExpanded, setIsExpanded] = useState(true)
  const [employerQuery, setEmployerQuery] = useState('')

  // What the directory knows about how this employer answers verifications.
  // Shown as a plain row like the other facts; only a platform-only carrier
  // also warns on its contact rows, the same way an opted-out address does.
  const { data: carrierData } = useQuery<
    CarrierByDotNumber,
    CarrierByDotNumberVariables
  >(GET_CARRIER_BY_DOT_NUMBER, {
    variables: { dotNumber: voe.dotNumber ?? '' },
    skip: !voe.dotNumber,
  })
  const carrier = carrierData?.carrierByDotNumber
  const carrierPlatforms = orderPlatforms(carrier?.verificationPlatforms)
  // The hover only says which platform is preferred. Platform-only is an
  // outreach rule, not a label for the recruiter, and notes can run long.
  const platformBadgeTooltip = (platform: (typeof carrierPlatforms)[number]) =>
    platform.preferred ? 'Preferred verification platform' : ''

  const schema = z.object({
    dotNumber: z
      .string()
      .max(50, { message: 'DoT number should have at most 50 letters' })
      .optional()
      .or(z.literal('')),
    contactName: z
      .string()
      .max(50, { message: 'Contact name should have at most 50 letters' })
      .optional()
      .or(z.literal('')),
    // Same rule the API applies, so the form never accepts an address the
    // save would refuse.
    email: z
      .string()
      .refine((data) => data.trim() === '' || isValidEmail(data), {
        message: 'Invalid email',
      })
      .optional(),
    phoneNumber: z
      .string()
      .refine(
        (data) =>
          unformatPhoneNumber(removeCountryCode(data)).length === 10 ||
          unformatPhoneNumber(removeCountryCode(data)).length === 0,
        {
          message: 'Phone number should have 10 digits',
        }
      )
      .optional()
      .or(z.literal('')),
    faxNumber: z
      .string()
      .refine(
        (data) =>
          unformatPhoneNumber(removeCountryCode(data)).length === 10 ||
          unformatPhoneNumber(removeCountryCode(data)).length === 0,
        {
          message: 'Phone number should have 10 digits',
        }
      )
      .optional()
      .or(z.literal('')),
    // Same rule the API applies, so a save is never rejected server-side for
    // a number the form accepted.
    referredPhone: z
      .string()
      .refine((data) => data === '' || isDialableUsNumber(data), {
        message: 'Phone number should have 10 digits and a valid area code',
      })
      .optional()
      .or(z.literal('')),
    referredExtension: z
      .string()
      .regex(/^\d{0,6}$/, { message: 'Extension should be 1 to 6 digits' })
      .optional()
      .or(z.literal('')),
  })

  const referralValues = (r?: VoEReferral | null) => ({
    referredPhone: r?.phoneNumber || '',
    referredExtension: r?.extension || '',
  })

  const form = useForm<VoeDetailsFormValues>({
    initialValues: {
      dotNumber: voe.dotNumber || '',
      contactName: voe.contactName || '',
      email: voe.email || '',
      phoneNumber: voe.phoneNumber || '',
      faxNumber: voe.faxNumber || '',
      ...referralValues(referral),
    },
    // The resolver is typed against a loose record. The schema fields match
    // VoeDetailsFormValues.
    validate: zodResolver(schema) as (
      values: VoeDetailsFormValues
    ) => FormErrors,
  })

  // What automation will do with the contact being entered, said at entry
  // time so the recruiter does not send an email the chain is about to send.
  // The chain does not start for a No Contact employer and skips a VoE that
  // is already closed, so there is nothing to promise for either.
  const { config: outreachConfig } = useCompanyOutreachConfig()
  const chainCanReach =
    voe.shouldContactEmployer !== false &&
    !OUTREACH_CLOSED_STATUSES.includes(voe.status)
  const entryPreview =
    isEditing && chainCanReach
      ? outreachPreview(outreachConfig, {
          hasEmail: Boolean(form.values.email?.trim()),
          // PhoneInput can hold a bare country code; count digits, not text.
          hasPhone:
            unformatPhoneNumber(
              removeCountryCode(form.values.phoneNumber ?? '')
            ).length > 0,
          // No end date on the request: the applicant still works here.
          isCurrentEmployer:
            !!voe.requestContent && voe.requestContent.endDate == null,
        })
      : null

  // Once per VoE per edit session: the first time the line appears. Holds
  // the VoE it was reported for; leaving edit mode starts a new session.
  const previewReportedForRef = useRef<string | null>(null)
  const previewTone = entryPreview?.tone ?? null
  useEffect(() => {
    if (!isEditing) {
      previewReportedForRef.current = null
      return
    }
    if (!previewTone || previewReportedForRef.current === voe.voeId) return
    previewReportedForRef.current = voe.voeId
    datadogRum.addAction('voe_outreach_preview_shown', {
      surface: 'dashboard',
      component: 'VerificationDetailsSection',
      trigger: outreachConfig?.outreachTrigger ?? null,
      tone: previewTone,
      voe_id: voe.voeId,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing, previewTone, voe.voeId])

  const [updateVoEReferral] = useMutation(UPDATE_VOE_REFERRAL, {
    refetchQueries: onRender || [],
    onError: (error) => {
      toast(
        error.message ||
          'Unable to update the referred number, please try again.',
        'error'
      )
    },
  })

  const [updateVoE, { loading }] = useMutation(UPDATE_VOE, {
    refetchQueries: onRender || [],
    onCompleted: (data: {
      updateVerificationOfEmployment?: Partial<VerificationOfEmployment> | null
    }) => {
      toast('Verification details updated successfully', 'success')
      setIsEditing(false)
      // Ensure the form reflects the just-saved values immediately,
      // even before any refetch completes.
      const updated = data?.updateVerificationOfEmployment
      if (updated) {
        form.setValues({
          dotNumber: updated.dotNumber || '',
          contactName: updated.contactName || '',
          email: updated.email || '',
          phoneNumber: updated.phoneNumber || '',
          faxNumber: updated.faxNumber || '',
          ...referralValues(referral),
        })
      }
      form.resetDirty()
    },
    onError: (error) => {
      toast(
        error.message ||
          'Unable to update verification details, please try again.',
        'error'
      )
    },
  })

  // Reset form when voe data changes
  useEffect(() => {
    form.setValues({
      dotNumber: voe.dotNumber || '',
      contactName: voe.contactName || '',
      email: voe.email || '',
      phoneNumber: voe.phoneNumber || '',
      faxNumber: voe.faxNumber || '',
      ...referralValues(referral),
    })
    form.resetDirty()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    voe.voeId,
    voe.dotNumber,
    voe.contactName,
    voe.email,
    voe.phoneNumber,
    voe.faxNumber,
    referral?.phoneNumber,
    referral?.extension,
  ])

  const handleSave = async () => {
    const validation = form.validate()
    if (validation.hasErrors) {
      return
    }

    const {
      referredPhone,
      referredExtension,
      dotNumber,
      contactName,
      email,
      phoneNumber,
      faxNumber,
    } = form.values

    // Captured before the referral await: a refetch from that mutation resets
    // the form, and the payload must stay what the recruiter submitted.
    const input = changedVoeInput(
      { dotNumber, contactName, email, phoneNumber, faxNumber },
      voe
    )

    // The referred number lives on the audit-trail row that heard it, so it
    // is saved separately from the VoE's own fields. It is saved first, so a
    // failed referral save keeps the form open instead of reporting success.
    // A blanked number removes the referral. Digit compare ignores the phone
    // input rewriting the stored number into its display form.
    const referralDirty =
      !samePhone(referredPhone, referral?.phoneNumber) ||
      !sameText(referredExtension, referral?.extension)
    if (referral?.auditTrailId && referralDirty) {
      const result = await updateVoEReferral({
        variables: {
          auditTrailId: referral.auditTrailId,
          input: { referredPhone, referredExtension },
        },
      })
      // With onError set, Apollo resolves instead of throwing; no data means
      // the mutation failed and its toast has already been shown.
      if (!result?.data?.updateVoEReferral) return
    }

    // Nothing on the VoE changed: do not send the form's blanks. A referral
    // save still closes the editor. A click with no edits closes it too,
    // without a request.
    if (Object.keys(input).length === 0) {
      if (referral?.auditTrailId && referralDirty) {
        toast('Verification details updated successfully', 'success')
      }
      setIsEditing(false)
      return
    }

    updateVoE({
      variables: {
        id: voe.voeId,
        input,
      },
    })
  }

  const handleCancel = () => {
    // Set values back to original
    form.setValues({
      dotNumber: voe.dotNumber || '',
      contactName: voe.contactName || '',
      email: voe.email || '',
      phoneNumber: voe.phoneNumber || '',
      faxNumber: voe.faxNumber || '',
      ...referralValues(referral),
    })

    // Use setTimeout to ensure form state updates are processed
    setTimeout(() => {
      form.resetDirty()
    }, 0)
    setIsEditing(false)
  }

  const handleStartEditing = () => {
    setEmployerQuery(voe.companyName || '')
    setIsEditing(true)
  }

  const handleEmployerPick = (company?: {
    dotNumber?: string | null
    companyName?: string | null
  }) => {
    if (!company?.dotNumber) return
    form.setFieldValue('dotNumber', String(company.dotNumber))
    if (company.companyName) setEmployerQuery(company.companyName)
  }

  // Handle DoT number click to open FMCSA link
  const handleDotNumberClick = () => {
    const dotNumber = form.values.dotNumber || voe.dotNumber
    if (dotNumber) {
      window.open(
        buildFmcsaCarrierUrl(dotNumber),
        '_blank',
        'noopener,noreferrer'
      )
    }
  }

  // Contacts the directory holds for a DOT: the stored VoE's in read mode (the
  // block), the one being typed in edit mode (the hint under Contact Name).
  // Same query and variables as the lookup above and the picker's, so the
  // Apollo cache serves them from one request.
  const storedDotNumber = voe.dotNumber?.trim() || ''
  const formDotNumber = (form.values.dotNumber as string)?.trim() || ''
  const knownContacts = useKnownCarrierContacts(
    isEditing ? formDotNumber : storedDotNumber
  )

  // Read mode only: a VoE with no contact at all, a DOT to look it up by, and
  // at least one reachable contact held for that DOT. Not gated on status: a
  // closed VoE can still get its contact recorded.
  const voeHasContact = [voe.contactName, voe.email, voe.phoneNumber].some(
    (value) => Boolean(value?.trim())
  )
  const showKnownContacts =
    !isEditing &&
    Boolean(storedDotNumber) &&
    !voeHasContact &&
    knownContacts.contacts.length > 0

  // Edit mode: "N contacts for DOT …" under Contact Name, once the count for
  // the DOT in the form has loaded.
  const contactHintCount =
    formDotNumber && !knownContacts.loading ? knownContacts.count : undefined

  /**
   * "Use" on the read-mode block: saves the top contact onto the VoE straight
   * away, without entering edit mode. Only contact fields the directory
   * actually has are sent, through the same mutation (and toasts) as the
   * section's own save. A contact with no email must not clear one that
   * landed after this block was shown. The refetch then gives the VoE a
   * contact and the block goes away.
   */
  const handleUseContact = async (contact: KnownContact) => {
    const knownCount = knownContacts.count
    const input: {
      contactName?: string
      email?: string
      phoneNumber?: string
    } = {}
    const contactName = nonBlank(contact.contactName)
    const email = nonBlank(contact.email)
    const phoneNumber = nonBlank(contact.phoneNumber)
    if (contactName) input.contactName = contactName
    if (email) input.email = email
    if (phoneNumber) input.phoneNumber = phoneNumber
    if (Object.keys(input).length === 0) return

    let saved: boolean
    try {
      const result = await updateVoE({
        variables: {
          id: voe.voeId,
          input,
        },
      })
      // With onError set, Apollo resolves instead of throwing; no data means
      // the mutation failed and its toast has already been shown.
      saved = Boolean(result?.data?.updateVerificationOfEmployment)
    } catch (error) {
      return
    }
    if (!saved) return

    trackContactAdded({
      component: CONTACT_COMPONENT,
      source: 'known_default',
      contact,
      knownCount,
      dotNumber: storedDotNumber || null,
    })
  }

  const handleContactSelected = (
    selected?: {
      contactName?: string | null
      phoneNumber?: string | null
      email?: string | null
    } | null
  ) => {
    if (!selected) return
    // A picked contact often has no email. Leaving the field alone keeps the
    // next save from clearing an email the VoE already has.
    const contactName = nonBlank(selected.contactName)
    const phoneNumber = nonBlank(selected.phoneNumber)
    const email = nonBlank(selected.email)
    if (contactName) form.setFieldValue('contactName', contactName)
    if (phoneNumber) form.setFieldValue('phoneNumber', phoneNumber)
    if (email) form.setFieldValue('email', email)
  }

  return (
    <>
      <Divider variant="dashed" />
      <div className="flex items-center justify-between">
        <button
          className="flex items-center gap-1 text-doubleNickel-gray-500 hover:text-doubleNickel-gray-700"
          onClick={() => setIsExpanded((v) => !v)}
        >
          {isExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
          <Text size="xs" fw={400} className="text-inherit">
            COMPANY INFORMATION
          </Text>
        </button>
        <div className="flex items-center gap-2">
          {isEditing ? (
            <>
              <ActionIcon
                aria-label="Save company information"
                size="xs"
                variant="subtle"
                color="green"
                onClick={handleSave}
                disabled={loading}
              >
                <Check size={14} />
              </ActionIcon>
              <ActionIcon
                size="xs"
                variant="subtle"
                color="red"
                onClick={handleCancel}
                disabled={loading}
              >
                <X size={14} />
              </ActionIcon>
            </>
          ) : (
            <ActionIcon
              aria-label="Edit company information"
              size="xs"
              variant="subtle"
              color="gray"
              onClick={handleStartEditing}
            >
              <Pencil size={12} />
            </ActionIcon>
          )}

          {loading && <Loader size={12} color="gray" />}
        </div>
      </div>

      <Collapse in={isExpanded}>
        <div className="space-y-2">
          {showKnownContacts && (
            <KnownContactsBlock
              dotNumber={storedDotNumber}
              contacts={knownContacts.contacts}
              onUse={handleUseContact}
              saving={loading}
            />
          )}

          {/* DOT Number - custom inline render for link styling */}
          {isEditing ? (
            <>
              <FormCompanyField
                label="Company"
                editing={true}
                formProps={{
                  value: employerQuery,
                  onChange: setEmployerQuery,
                  placeholder: 'Search by name or DOT',
                }}
                onSelect={handleEmployerPick}
              />
              <FormCompanyDotField
                label="DOT Number"
                editing={true}
                formProps={{
                  ...form.getInputProps('dotNumber'),
                  placeholder: 'Enter DOT number',
                }}
                onSelect={handleEmployerPick}
              />
            </>
          ) : (
            <div className="flex flex-row items-center justify-between">
              <Text size="xs" fw={300}>
                DOT Number
              </Text>
              <div className="flex w-[250px] items-center justify-end gap-2">
                {form.values.dotNumber ? (
                  <Text
                    size="xs"
                    fw={300}
                    className="cursor-pointer py-1.5 text-doubleNickel-brand-500 hover:underline"
                    onClick={handleDotNumberClick}
                  >
                    {form.values.dotNumber}
                  </Text>
                ) : (
                  <Text size="xs" fw={300} className="py-1.5">
                    -
                  </Text>
                )}
                {form.values.dotNumber && (
                  <Tooltip label="Open FMCSA carrier lookup" position="top">
                    <ActionIcon
                      size="xs"
                      variant="subtle"
                      color="gray"
                      onClick={handleDotNumberClick}
                    >
                      <ExternalLink size={12} />
                    </ActionIcon>
                  </Tooltip>
                )}
                {/* Internal admins only; renders nothing for recruiters. */}
                <CarrierDirectoryLink carrierId={carrier?.carrierId} />
              </div>
            </div>
          )}

          {carrierPlatforms.length > 0 && (
            <div className="flex flex-row items-start justify-between">
              <Text size="xs" fw={300} className="py-1.5">
                Platform
              </Text>
              {/* Preferred first and blue; the rest gray. */}
              <div className="flex w-[250px] flex-wrap items-center justify-end gap-1 py-1">
                {carrierPlatforms.map((platform) => (
                  <StyledBadge
                    key={platform.provider}
                    color={platform.preferred ? 'blue' : 'gray'}
                    compact="xs"
                    tooltipText={platformBadgeTooltip(platform)}
                  >
                    {voeProviderLabel(platform.provider)}
                  </StyledBadge>
                ))}
              </div>
            </div>
          )}

          {!isEditing && companyAddress && (
            <div className="flex flex-row items-center justify-between">
              <Text size="xs" fw={300}>
                Address
              </Text>
              <Text size="xs" fw={300} className="w-[250px] py-1.5 text-right">
                {formatAddress(companyAddress as any) || '-'}
              </Text>
            </div>
          )}

          {isEditing ? (
            <FormContactField
              dotNumber={form.values.dotNumber as string}
              label="Contact Name"
              editing={true}
              formProps={form.getInputProps('contactName')}
              onSelect={handleContactSelected}
              knownCount={contactHintCount}
            />
          ) : (
            <div className="flex flex-row items-center justify-between">
              <Text size="xs" fw={300}>
                Contact Name
              </Text>
              <Text size="xs" fw={300} className="w-[250px] py-1.5 text-right">
                {form.values.contactName || '-'}
              </Text>
            </div>
          )}

          {/* Email - custom inline render with Send action icon */}
          {isEditing ? (
            <FormTextField
              label="Email"
              editing={true}
              formProps={form.getInputProps('email')}
            />
          ) : (
            <div className="flex flex-row items-center justify-between">
              <Text size="xs" fw={300}>
                Email
              </Text>
              <div className="flex w-[250px] items-center justify-end gap-2">
                {form.values.email ? (
                  <Text
                    size="xs"
                    fw={300}
                    className="cursor-pointer py-1.5 text-doubleNickel-brand-500 hover:underline"
                    onClick={() => onEmail?.()}
                  >
                    {form.values.email}
                  </Text>
                ) : (
                  <Text size="xs" fw={300} className="py-1.5">
                    -
                  </Text>
                )}
                {form.values.email && voe.contactSuppression?.email && (
                  <Tooltip
                    label="This contact asked not to be emailed"
                    position="top"
                  >
                    <MailX
                      aria-label="Email suppressed"
                      size={12}
                      className="flex-shrink-0 text-doubleNickel-warning-500"
                    />
                  </Tooltip>
                )}
                {form.values.email && (
                  <Tooltip label="Send VoE email" position="top">
                    <ActionIcon
                      size="xs"
                      variant="subtle"
                      color="gray"
                      onClick={() => onEmail?.()}
                    >
                      <Send size={12} />
                    </ActionIcon>
                  </Tooltip>
                )}
              </div>
            </div>
          )}

          {/* Phone - custom inline render with Phone action icon */}
          {isEditing ? (
            <FormPhoneField
              label="Phone"
              editing={true}
              formProps={form.getInputProps('phoneNumber')}
            />
          ) : (
            <div className="flex flex-row items-center justify-between">
              <Text size="xs" fw={300}>
                Phone
              </Text>
              <div className="flex w-[250px] items-center justify-end gap-0.5">
                {form.values.phoneNumber ? (
                  <Text
                    size="xs"
                    fw={300}
                    className="cursor-pointer py-1.5 text-doubleNickel-brand-500 hover:underline"
                    onClick={() => onCall?.()}
                  >
                    {formatInternationalPhoneNumber(form.values.phoneNumber)}
                  </Text>
                ) : (
                  <Text size="xs" fw={300} className="py-1.5">
                    -
                  </Text>
                )}
                {form.values.phoneNumber &&
                  voe.contactSuppression?.phoneNumber && (
                    <Tooltip
                      label="This contact asked not to be called"
                      position="top"
                    >
                      <PhoneOff
                        aria-label="Phone suppressed"
                        size={12}
                        className="flex-shrink-0 text-doubleNickel-warning-500"
                      />
                    </Tooltip>
                  )}
                {form.values.phoneNumber && (
                  <Tooltip label="Call this number" position="top">
                    <ActionIcon
                      size="xs"
                      variant="subtle"
                      color="gray"
                      onClick={() => onCall?.()}
                    >
                      <Phone size={12} />
                    </ActionIcon>
                  </Tooltip>
                )}
                <VoEVoiceAgentButton
                  voeId={voe.voeId}
                  phoneNumber={form.values.phoneNumber || voe.phoneNumber}
                  email={form.values.email || voe.email}
                  canceledOutOfWindow={voe.canceledOutOfWindow}
                  callingHours={voe.callingHours}
                  guardContact={guardContact}
                />
              </div>
            </div>
          )}

          {isEditing && <OutreachPreviewLine preview={entryPreview} />}

          {/* Referred Number - a number an earlier agent call was handed; the
              VoE's own Phone stays as the driver gave it (ENG-4702) */}
          {isEditing && referral && (
            <>
              <FormPhoneField
                label="Referred Number"
                editing={true}
                formProps={form.getInputProps('referredPhone')}
              />
              <FormTextField
                label="Referred Ext."
                editing={true}
                formProps={form.getInputProps('referredExtension')}
              />
            </>
          )}
          {!isEditing && referral && (
            <div
              className="flex flex-row items-center justify-between"
              data-testid="voe-referred-number-row"
            >
              <Tooltip label={describeReferral(referral)} position="top-start">
                <Text size="xs" fw={300}>
                  Referred Number
                </Text>
              </Tooltip>
              <div className="flex w-[250px] items-center justify-end gap-0.5">
                <Text
                  size="xs"
                  fw={300}
                  className="cursor-pointer py-1.5 text-doubleNickel-brand-500 hover:underline"
                  onClick={() => onCall?.(referral.phoneNumber)}
                >
                  {formatInternationalPhoneNumber(referral.phoneNumber)}
                  {referral.extension && (
                    <span className="ml-1 text-doubleNickel-gray-500">
                      ext {referral.extension}
                    </span>
                  )}
                </Text>
                <Tooltip label="Call this number" position="top">
                  <ActionIcon
                    size="xs"
                    variant="subtle"
                    color="gray"
                    onClick={() => onCall?.(referral.phoneNumber)}
                  >
                    <Phone size={12} />
                  </ActionIcon>
                </Tooltip>
                <VoEVoiceAgentButton
                  voeId={voe.voeId}
                  phoneNumber={form.values.phoneNumber || voe.phoneNumber}
                  email={form.values.email || voe.email}
                  canceledOutOfWindow={voe.canceledOutOfWindow}
                  callingHours={voe.referredCallingHours ?? voe.callingHours}
                  guardContact={guardContact}
                  targetPhoneNumber={referral.phoneNumber}
                  tooltip="Call the referred number via the voice agent"
                />
              </div>
            </div>
          )}

          {isEditing ? (
            <FormPhoneField
              label="Fax Number"
              editing={true}
              formProps={form.getInputProps('faxNumber')}
            />
          ) : (
            <div className="flex flex-row items-center justify-between">
              <Text size="xs" fw={300}>
                Fax Number
              </Text>
              <div className="flex w-[250px] items-center justify-end gap-2">
                {form.values.faxNumber && voe.contactSuppression?.faxNumber && (
                  <Tooltip
                    label="This contact asked not to be faxed"
                    position="top"
                  >
                    <PhoneOff
                      aria-label="Fax suppressed"
                      size={12}
                      className="flex-shrink-0 text-doubleNickel-warning-500"
                    />
                  </Tooltip>
                )}
                <Text size="xs" fw={300} className="py-1.5 text-right">
                  {form.values.faxNumber
                    ? formatInternationalPhoneNumber(form.values.faxNumber)
                    : '-'}
                </Text>
              </div>
            </div>
          )}
        </div>
      </Collapse>
    </>
  )
}

export default VerificationDetailsSection
